import { type Config } from '@common/clients/api/generated/client';
import { type ContextData } from '@common/defaults/ContextData';
import { type PartialUserContext } from '@common/defaults/UserContext';
import { isClientSide } from '@common/utils/isClientSide';

import { getCacheConfig } from './utils/getCacheConfig';
import { withUploadProgress } from './utils/withUploadProgress';
import { type BaseClientOptions, createBaseClient } from './BaseClient';
import { type ApiFetchOptions } from './types';

const getCookieHeader = (userContext: PartialUserContext | undefined) => {
    const cookies = {
        sAccessToken: userContext?.supertokens?.sAccessToken,
        sRefreshToken: userContext?.supertokens?.sRefreshToken,
        sFrontToken: userContext?.supertokens?.sFrontToken,
        sAntiCsrf: userContext?.supertokens?.sAntiCsrf,
        'st-last-access-token-update': userContext?.supertokens?.['st-last-access-token-update'],
    } as const;

    return {
        Cookie: Object.entries(cookies)
            .filter(([_key, value]) => value)
            .map((entry) => entry.join('='))
            .join('; '),
    };
};

const DEFAULT_TIMEOUT = 10000;

interface CreateApiClientContextData {
    cacheBuster?: string;
    config: ContextData['config'];
    env: ContextData['env'];
}

export interface CreateApiClientConfig {
    contextData: CreateApiClientContextData;
    userContext?: PartialUserContext;
    fetchOptions?: Config & ApiFetchOptions;
    onUploadProgress?: (progress: number) => void;
}

export const createApiClient = ({
    contextData,
    userContext,
    fetchOptions,
    onUploadProgress,
}: CreateApiClientConfig) => {
    const { config } = contextData;
    const appEnv = contextData.env?.APP_ENV || process.env.APP_ENV || process.env.NODE_ENV;
    const isDevEnv = appEnv === 'development';

    if (!config?.api) {
        throw new Error('contextData.config.api not yet set');
    }

    /**
     * @description override the fetch function if onUploadProgress is provided
     * since fetch doesnt support progress events
     * @see: @common/clients/request/request-utils.ts
     */
    if (onUploadProgress) {
        fetchOptions = {
            ...fetchOptions,
            fetch: withUploadProgress(onUploadProgress),
        };
    }

    const cookieHeader = !isClientSide ? getCookieHeader(userContext) : undefined;
    const internalUrl = !isClientSide && contextData.config.api.internalHost;
    const baseUrl = internalUrl || contextData.config.api.host;

    const clientConfig = {
        useNextClient: true,
        useNewRelic: true,
        useStatusCheck: true,
    } satisfies BaseClientOptions;

    const credentialConfig = {
        credentials: !isClientSide || isDevEnv ? 'include' : 'same-origin',
    } satisfies RequestInit;

    const defaultHeaders = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...cookieHeader,
    } satisfies HeadersInit;

    const cacheConfig = getCacheConfig(contextData, fetchOptions);

    const client = createBaseClient(clientConfig, {
        userContext,
        fetchOptions: {
            ...credentialConfig,
            ...fetchOptions,
            next: cacheConfig.next,
            baseUrl,
            timeout: fetchOptions?.timeout || DEFAULT_TIMEOUT,
            headers: {
                ...defaultHeaders,
                // eslint-disable-next-line @typescript-eslint/no-misused-spread
                ...fetchOptions?.headers,
                ...cacheConfig.headers,
            },
        },
    });

    return client;
};
